import dayjs from 'dayjs';
import { useMemo } from 'react';

import type { LocationHistoryMinimal } from '~hooks/useLocationHistory/models';

import { getCycleSegmentType, Segment } from './useGetCycleSegments';

// Mirrors the threshold used by useGetCycleSegments to avoid drawing idle
// blocks across periods where the device was effectively offline.
const NO_SIGNAL_THRESHOLD_MINUTES = 15;

/**
 * Builds idle segments to overlay on the cycle timeline. Consecutive idle
 * pings are grouped into a single segment and split whenever the cycle
 * segment type changes or there is a gap in pings exceeding the
 * no-signal threshold. Segments with only a single ping are dropped to
 * match the cycle segment behavior, since they would render as a sliver.
 */
const useGetIdleSegments = (pings: LocationHistoryMinimal[]): Segment[] =>
  useMemo(() => {
    const groups: LocationHistoryMinimal[][] = [];
    let currentGroup: LocationHistoryMinimal[] = [];

    pings.forEach((ping) => {
      if (!ping.idle) {
        if (currentGroup.length > 0) {
          groups.push(currentGroup);
          currentGroup = [];
        }
        return;
      }

      if (currentGroup.length === 0) {
        currentGroup.push(ping);
        return;
      }

      const lastPing = currentGroup[currentGroup.length - 1];
      const sameSegmentType = getCycleSegmentType(lastPing) === getCycleSegmentType(ping);
      const withinSignal =
        dayjs(ping.occurredAt).diff(dayjs(lastPing.occurredAt), 'minutes') <
        NO_SIGNAL_THRESHOLD_MINUTES;

      if (sameSegmentType && withinSignal) {
        currentGroup.push(ping);
      } else {
        groups.push(currentGroup);
        currentGroup = [ping];
      }
    });

    if (currentGroup.length > 0) {
      groups.push(currentGroup);
    }

    return groups
      .filter((g) => g.length > 1)
      .map((g) => ({
        start: g[0].occurredAt,
        end: g[g.length - 1].occurredAt,
        type: getCycleSegmentType(g[0]),
      }));
  }, [pings.length]);

export { useGetIdleSegments };
